import React, { useState, useEffect } from 'react';
import { Box, CircularProgress, Typography } from '@mui/material';
import Axios from '../../../config/axios';

interface AuthenticatedImageProps {
    src: string;
    alt?: string;
    onClick?: () => void;
    sx?: any;
    fallbackContent?: React.ReactNode;
}

const AuthenticatedImage: React.FC<AuthenticatedImageProps> = ({
    src,
    alt = 'Image',
    onClick,
    sx,
    fallbackContent,
}) => {
    const [imageUrl, setImageUrl] = useState<string | null>(null);
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<boolean>(false);

    useEffect(() => {
        let isMounted = true;
        let objectUrl: string | null = null;

        const fetchImage = async () => {
            try {
                setLoading(true);
                setError(false);

                // Fetch image with authentication
                const response = await Axios.get(src, {
                    responseType: 'blob',
                    timeout: 30000, // 30 second timeout
                });

                if (isMounted) {
                    // Create blob URL
                    objectUrl = URL.createObjectURL(response.data);
                    setImageUrl(objectUrl);
                    setLoading(false);
                }
            } catch (err) {
                console.error('Failed to load authenticated image:', err);
                if (isMounted) {
                    setError(true);
                    setLoading(false);
                }
            }
        };

        if (src) {
            fetchImage();
        }

        // Cleanup function
        return () => {
            isMounted = false;
            if (objectUrl) {
                URL.revokeObjectURL(objectUrl);
            }
        };
    }, [src]);

    if (loading) {
        return (
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    minHeight: '200px',
                    ...sx,
                }}
            >
                <CircularProgress size={40} />
            </Box>
        );
    }

    if (error || !imageUrl) {
        return (
            fallbackContent || (
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        minHeight: '200px',
                        color: '#FFFFFF66',
                        ...sx,
                    }}
                >
                    <svg
                        width='48'
                        height='48'
                        viewBox='0 0 24 24'
                        fill='none'
                        stroke='currentColor'
                        strokeWidth='2'
                    >
                        <rect x='3' y='3' width='18' height='18' rx='2' ry='2' />
                        <circle cx='8.5' cy='8.5' r='1.5' />
                        <polyline points='21 15 16 10 5 21' />
                    </svg>
                    <Typography sx={{ mt: 2, fontSize: '14px' }}>
                        Failed to load image
                    </Typography>
                </Box>
            )
        );
    }

    return (
        <Box
            component='img'
            src={imageUrl}
            alt={alt}
            onClick={onClick}
            sx={{
                maxWidth: '100%',
                maxHeight: '100%',
                objectFit: 'contain',
                padding: 1,
                ...sx,
            }}
        />
    );
};

export default AuthenticatedImage;